import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  TASK_TYPE_LABELS_FR,
  type Task,
  type TaskType,
} from "@relais/shared";
import { getDb } from "@relais/offline";
import { useOffline } from "../../lib/offline-context.js";
import { useSession } from "../../lib/auth.js";
import { ReceptionSupportsForm } from "../../features/reception-supports/ReceptionSupportsForm.js";
import { ReceptionChantierForm } from "../../features/reception-chantier/ReceptionChantierForm.js";
import { SuiviChantierForm } from "../../features/suivi-chantier/SuiviChantierForm.js";
import { PvReceptionForm } from "../../features/pv-reception/PvReceptionForm.js";
import { SavForm } from "../../features/sav/SavForm.js";
import { RenderLockedNotice } from "../../features/task-detail/RenderLockedNotice.js";

// Types qui ont un sous-arbre dédié → on redirige le bare /render vers leur
// première sous-route. Les autres types restent rendus inline par RenderPage.
const RENDER_SUBROUTE_BY_TYPE: Partial<Record<TaskType, "/tasks/$taskId/render/vt">> = {
  vt: "/tasks/$taskId/render/vt",
};

export const Route = createFileRoute("/tasks/$taskId/render")({
  // Dispatch uniquement sur l'URL bare /render. Sur une URL profonde (cold-open
  // PWA hors-ligne sur /render/vt/<section>/<sub> par ex), pathname diffère du
  // prefix → no-op, on préserve la deep link.
  beforeLoad: async ({ params, location }) => {
    const bare = `/tasks/${params.taskId}/render`;
    if (location.pathname !== bare && location.pathname !== `${bare}/`) return;
    const db = getDb();
    const row = await db.withRetry(() => db.tasks.get(params.taskId));
    const sub = row?.data?.type ? RENDER_SUBROUTE_BY_TYPE[row.data.type] : undefined;
    if (sub) {
      throw redirect({ to: sub, params: { taskId: params.taskId }, replace: true });
    }
  },
  component: RenderPage,
});

// Champs minimum par type — point d'extension pour vrais formulaires détaillés.
const FIELDS_BY_TYPE: Record<TaskType, Array<{ key: string; label: string; multiline?: boolean }>> = {
  vt: [
    { key: "constat_lieux", label: "Constat des lieux", multiline: true },
    { key: "metres_lineaires", label: "Mètres linéaires" },
    { key: "remarques", label: "Remarques", multiline: true },
  ],
  suivi_chantier: [], // rendered by <SuiviChantierForm>, not the inline fallback
  reception_supports: [
    { key: "supports_recus", label: "Supports reçus", multiline: true },
    { key: "ecarts", label: "Écarts vs commande", multiline: true },
  ],
  reception_chantier: [],
  pv_reception: [], // rendered by <PvReceptionForm>, not the inline fallback
  levee_reserve: [], // rendered by <PvReceptionForm> (signatures only)
  sav: [], // rendered by <SavForm>, not the inline fallback
};

function RenderPage() {
  const { taskId } = Route.useParams();
  const { service, pushToast } = useOffline();
  const session = useSession();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!service) return;
    service.getTask(taskId).then(setTask);
  }, [service, taskId]);

  if (!task) return <p>…</p>;

  if (
    task.status === "completed" ||
    task.status === "cancelled" ||
    task.status === "refused"
  ) {
    return <RenderLockedNotice task={task} />;
  }

  if (task.type === "vt") {
    // Child route (/render/vt/...) renders inside this Outlet. La redirection
    // depuis /render bare vers /render/vt est gérée en beforeLoad.
    return <Outlet />;
  }

  if (task.type === "reception_supports") {
    if (session.status !== "authed") return <p>…</p>;
    return <ReceptionSupportsForm task={task} userId={session.session.user.id} />;
  }

  if (task.type === "reception_chantier") {
    if (session.status !== "authed") return <p>…</p>;
    return <ReceptionChantierForm task={task} userId={session.session.user.id} />;
  }

  if (task.type === "suivi_chantier") {
    if (session.status !== "authed") return <p>…</p>;
    return <SuiviChantierForm task={task} userId={session.session.user.id} />;
  }

  // Levée de réserve : même payload que le PV, réduit à l'étape signatures
  // (voir PvReceptionForm). Sans ce branchement, la page tombait sur le repli
  // `FIELDS_BY_TYPE` vide : aucun champ, aucune signature.
  if (task.type === "pv_reception" || task.type === "levee_reserve") {
    if (session.status !== "authed") return <p>…</p>;
    return <PvReceptionForm task={task} userId={session.session.user.id} />;
  }

  if (task.type === "sav") {
    if (session.status !== "authed") return <p>…</p>;
    return <SavForm task={task} userId={session.session.user.id} />;
  }

  // All known task types are handled above; this is a safety fallback for
  // future types. Cast to TaskType to prevent TS narrowing to never.
  const fields = FIELDS_BY_TYPE[task.type as TaskType];

  async function save() {
    if (!service) return;
    setSaving(true);
    await service.enqueue({
      kind: "update_render",
      task_id: taskId,
      payload: values,
    });
    setSaving(false);
    setDirty(false);
  }

  async function submit() {
    if (!service || !task) return;
    if (dirty) await save();
    const alreadySubmitted = task.status === "submitted";
    if (!alreadySubmitted) {
      await service.enqueue({ kind: "submit", task_id: taskId });
    }
    pushToast({
      kind: "success",
      message: alreadySubmitted
        ? "Modifications enregistrées — synchronisation automatique"
        : "Envoi enregistré — synchronisation automatique",
    });
    navigate({ to: "/tasks/$taskId", params: { taskId } });
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold">
          Rendu · {TASK_TYPE_LABELS_FR[task.type]}
        </h2>
        <p className="text-sm text-muted-foreground">
          {task.project_snapshot?.name ?? task.project_id}
        </p>
      </header>

      <form className="space-y-4" onSubmit={(e) => {
        e.preventDefault();
        save();
      }}>
        {fields.map((f) =>
          f.multiline ? (
            <label key={f.key} className="block">
              <span className="text-sm text-muted-foreground">{f.label}</span>
              <textarea
                value={values[f.key] ?? ""}
                onChange={(e) => {
                  setValues({ ...values, [f.key]: e.target.value });
                  setDirty(true);
                }}
                className="mt-1 w-full rounded-md border px-3 py-2 bg-background min-h-24"
              />
            </label>
          ) : (
            <label key={f.key} className="block">
              <span className="text-sm text-muted-foreground">{f.label}</span>
              <input
                value={values[f.key] ?? ""}
                onChange={(e) => {
                  setValues({ ...values, [f.key]: e.target.value });
                  setDirty(true);
                }}
                className="mt-1 w-full rounded-md border px-3 py-2 bg-background"
              />
            </label>
          ),
        )}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!dirty || saving}
            className="rounded-md border px-4 py-2"
          >
            {saving ? "…" : "Enregistrer"}
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-md bg-primary text-primary-foreground px-4 py-2"
          >
            Soumettre
          </button>
        </div>
      </form>
    </div>
  );
}
